import { useEffect, useRef } from "react";
import { AxiosError } from "axios";
import axiosInstance from "src/config/axios";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { GenericResponse } from "../base.type";
import {
  Invite,
  JobDetail,
  MatchedTalents,
  Milestone,
  ProjectFundingInfo,
  Regions,
  LocationTabs,
} from "./hire.type";
import { OverAllRating } from "../profile/profile.type";

export const MATCHING_IN_PROGRESS = "Matching in progress";

/**
 * True while the API is (re)matching this job: it writes "Matching in
 * progress" to matching_info when a run starts and the real report when it
 * ends. Polls every 2s only while in progress, then calls `onDone` once.
 */
export const useMatchingInProgress = (jobId?: string, onDone?: () => void) => {
  const { data } = useSWR<JobDetail, AxiosError, any>(
    jobId ? `/dev/getAJobDetails/${jobId}` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data.data[0]),
    {
      refreshInterval: (latest) => ((latest as any)?.matching_info === MATCHING_IN_PROGRESS ? 2000 : 0),
    }
  );
  const inProgress = (data as any)?.matching_info === MATCHING_IN_PROGRESS;
  const wasInProgress = useRef(false);
  useEffect(() => {
    if (wasInProgress.current && !inProgress) onDone && onDone();
    wasInProgress.current = inProgress;
  }, [inProgress, onDone]);
  return inProgress;
};

/** Location tabs for a gated job: the stated location plus each wider ring, with qualified counts. */
export const useLocationTabsQuery = (jobId?: string) => {
  const { data, error, isLoading, mutate } = useSWR<LocationTabs, AxiosError, any>(
    jobId ? `/comp/getLocationTabs/${jobId}` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data.data)
  );
  return { locationTabs: data, isLoading, error, getLocationTabs: mutate };
};

export const useAllMatchedTalentPerJobReqQuery = (
  jobId?: string,
  page?: number,
  pageSize?: number,
  status?: string,
  queryParams?: string,
) => {
  const { data, error, isLoading, mutate,isValidating } = useSWR<
    MatchedTalents["data"],
    AxiosError,
    any
  >(
    [
      `/comp/getAllMatchedTalentPerJobReq/${jobId}${
        status ? `/${status}` : ""
      }?page=${page ?? 1}&limit=${pageSize ?? 10}`,
      queryParams,
    ],
    ([url, queryParams]: any) =>
      axiosInstance.get(url + (queryParams || "")).then((res) => res.data.data),
    {
      keepPreviousData: true,
    }
  );


  return {
    matchedTalent: data?.data ?? [],
    totalCount: data?.total ?? 0,
    isLoading,
    isValidating,
    error,
    getMatchedTalent: mutate,
  };
};

export const useMatchedTalentQuery = (jobId?: string, talentId?: string) => {
  const { data, error, isLoading, mutate } = useSWR<any, AxiosError, any>(
    `/comp/getAMatchedTalent/${jobId}/${talentId}`,
    (url: string) => axiosInstance.get(url).then((res) => res.data.data[0])
  );

  return {
    matchedTalent: data,
    isLoading,
    error,
    getMatchedTalent: mutate,
  };
};




export const useSearchMatchedTalentQuery = (
  searchQuery?: string,
  page?: number,
  pageSize?: number,
  jobId?: string
) => {

  const { data, error, isLoading, isValidating, mutate } = useSWR<any, AxiosError>(
    searchQuery && jobId
      ? `/comp/searchForMatchedTalents?searchBy=${searchQuery}&page=${page}&limit=${pageSize}&job_id=${jobId}`
      : null,
    (url: string) =>
      axiosInstance.get(url).then((res) => res.data.data ?? []),
    {
      keepPreviousData: true,
      revalidateOnFocus: false,
    }
  );

  return {
    searchedTalent: data?.data ?? [],
    totalCount: data?.total ?? 0,
    isLoading,
    isValidating,
    error,
    getMatchedTalent: mutate,
  };
};


type TogglePinTalentArg = { talentID: string; jobID: string };
type TogglePinTalentResponse = { status: boolean; message: string; data: any };

export const useToggleShortlistTalentMutation = () => {
  const { data, trigger, error, isMutating } = useSWRMutation(
    "/comp/toggleShortlistTalent",
    async (url: string, { arg }: { arg: TogglePinTalentArg }) => {
      const res = await axiosInstance.put(`${url}/${arg.talentID}/${arg.jobID}`);
      return res.data as TogglePinTalentResponse;
    }
  );

  return {
    data,
    toggleShortlistTalent: trigger,
    error,
    isLoading: isMutating,
  };
};

export const useTogglePinTalentMutation = () => {
  const { data, trigger, error, isMutating } = useSWRMutation(
    "/comp/togglePinTalent", 
    async (url: string, { arg }: { arg: TogglePinTalentArg }) => {
      const res = await axiosInstance.put(`${url}/${arg.talentID}/${arg.jobID}`);
      return res.data as TogglePinTalentResponse;
    }
  );

  return {
    data,
    togglePinTalent: trigger, 
    error,
    isLoading: isMutating,
  };
};





export const useRecentMatchedTalentPerJobQuery = (queryParams = "") => {
  const { data, error, isLoading, mutate } = useSWR<
    MatchedTalents["data"],
    AxiosError,
    [string, string]
  >(
    ["/comp/getRecentMatchedTalentPerJobReq", queryParams],
    ([url, queryParams]) =>
      axiosInstance.get(url + (queryParams || "")).then((res) => res.data.data)
  );

  return {
    matchedTalents: data,
    isLoading,
    error,
    totalCount: data?.total ?? 0,
    getMatchedTalents: mutate,
  };
};

export const useJobDetailQuery = (id: string) => {
  const { data, error, isLoading, mutate } = useSWR<
    JobDetail,
    AxiosError,
    string
  >(`/dev/getAJobDetails/${id}`, (url: string) =>
    axiosInstance.get(url).then((res) => res.data.data[0])
  );

  return {
    jobDetail: data,
    isLoading,
    error,
    getJobDetail: mutate,
  };
};

export const useJobDetailsQuery = (page: number, pageSize: number) => {
  const { data, error, isLoading, mutate } = useSWR<
    any,
    AxiosError,
    [string, string]
  >(
    ["/comp/getJobDetails", `?page=${page}&limit=${pageSize}`],
    ([url, queryParams]) =>
      axiosInstance.get(url + (queryParams || "")).then((res) => res.data.data)
  );

  return {
    jobDetails: data,
    isLoading,
    error,
    getJobDetails: mutate,
  };
};

export const useRegionsQuery = (projectId?: string) => {
  const { data, error, isLoading, mutate } = useSWR<
    Regions["data"],
    AxiosError,
    string
  >(`/all/getPricingRegion`, (url: string) =>
    axiosInstance.get(url).then((res) => res.data?.data)
  );

  return {
    regions: data,
    isLoading,
    error,
    getRegions: mutate,
  };
};

export const useHireContactMutation = () => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    MatchedTalents["data"],
    AxiosError,
    `/comp/hireContact`,
    FormData,
    any
  >(`/comp/hireContact`, (url: string, { arg }) =>
    axiosInstance.post(url, arg).then((res) => res.data.data)
  );

  return {
    data,
    isLoading: isMutating,
    hireContact: trigger,
  };
};

export const useInviteTalentMutation = ({
  userId,
  jobId,
}: {
  userId: string;
  jobId: string;
}) => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    {},
    AxiosError,
    string,
    FormData,
    any
  >(`/comp/inviteTalent/${userId}/${jobId}`, (url: string, { arg }) =>
    axiosInstance.put(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    sendInvite: trigger,
  };
};

export const useUserInviteQuery = (userId: string, JobDetailId: string) => {
  const { data, error, isLoading, mutate } = useSWR<Invite, AxiosError, string>(
    `/comp/getUserJobInvite/${userId}/${JobDetailId}`,
    (url: string) => axiosInstance.get(url).then((res) => res.data.data)
  );

  return {
    jobInvite: data,
    isLoading,
    error,
    getJobInvite: mutate,
  };
};

export const useProjectFundingInfoQuery = (projectId: string) => {
  const { data, error, isLoading, mutate } = useSWR<
    ProjectFundingInfo,
    AxiosError,
    string
  >(`/all/getProjectFundingInfo/${projectId}`, (url: string) =>
    axiosInstance.get(url).then((res) => res.data.data)
  );

  return {
    projectFundingInfo: data,
    isLoading,
    error,
    getProjectFundingInfo: mutate,
  };
};

export const useQuickHireMutation = () => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    GenericResponse,
    AxiosError,
    `/comp/quickHire`,
    FormData,
    any
  >(`/comp/quickHire`, (url: string, { arg }) =>
    axiosInstance.post(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    quickHire: trigger,
  };
};

export const useCreateMilestoneMutation = (id: string) => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    GenericResponse,
    AxiosError,
    string,
    FormData,
    any
  >(`/comp/createMilestone/${id}`, (url: string, { arg }) =>
    axiosInstance.put(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    quickHire: trigger,
  };
};

export const useDeleteMilestoneMutation = (id: string) => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    GenericResponse,
    AxiosError,
    string,
    FormData,
    any
  >(`/comp/deleteMilestone/${id}`, (url: string, { arg }) =>
    axiosInstance.delete(url).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    quickHire: trigger,
  };
};

export const useUpdateMilestoneStateMutation = (id: string, userId: string) => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    Milestone,
    AxiosError,
    string,
    FormData,
    any
  >(`/comp/updateMilestoneState/${id}/${userId}`, (url: string, { arg }) =>
    axiosInstance.put(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    updateMilestoneState: trigger,
  };
};

// export const useGiveOfferMutation = ({
//   userId,
//   jobId,
// }: {
//   userId: string;
//   jobId: string;
// }) => {
//   const { data, trigger, isMutating, error } = useSWRMutation<
//     GenericResponse,
//     AxiosError,
//     string,
//     FormData,
//     any
//   >(`/comp/giveOffer/${userId}/${jobId}`, (url: string, { arg }) =>
//     axiosInstance.put(url, arg).then((res) => res.data)
//   );

//   return {
//     data,
//     isLoading: isMutating,
//     giveOffer: trigger,
//   };
// };

export const useGiveOfferMutation = ({
  userId,
  jobId,
}: {
  userId: string;
  jobId: string;
}) => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    GenericResponse,
    AxiosError<{ message?: string }>,
    string,
    Record<string, any>,
    any
  >(`/comp/giveOffer/${userId}/${jobId}`, (url: string, { arg }: { arg: Record<string, any> }) =>
    axiosInstance.put(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    giveOffer: trigger,
  };
};


type UpdatePaymentPlanPayload = {
  payment_plan: "" | "basic" | "enterprise";
};

export const useUpdatePaymentPlan = (jobDetailID: string) => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    GenericResponse,
    AxiosError<{ message?: string }>,
    string,
    UpdatePaymentPlanPayload
  >(
    `/comp/updateJobDetailPaymentPlan/${jobDetailID}`,
    (url: string, { arg }: { arg: UpdatePaymentPlanPayload }) =>
      axiosInstance.put(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    updatePaymentPlan: trigger,
    error,
  };
};

export const useRateTalentMutation = () => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    OverAllRating,
    AxiosError,
    `/comp/rateTalent`,
    FormData,
    any
  >(`/comp/rateTalent`, (url: string, { arg }) =>
    axiosInstance.put(url, arg).then((res) => res.data)
  );

  return {
    data,
    isLoading: isMutating,
    rateTalent: trigger,
    error,
  };
};

// ─── Selection / Submit Selection ────────────────────────────────────────────

type SubmitSelectionArg = { jobId: string; talentIds: number[] };
type SubmitSelectionResponse = { status: boolean; message: string; data: any };

export const useSubmitSelectionMutation = () => {
  const { data, trigger, isMutating, error } = useSWRMutation<
    SubmitSelectionResponse,
    AxiosError,
    string,
    SubmitSelectionArg
  >(
    "/comp/submitSelection",
    async (_url: string, { arg }: { arg: SubmitSelectionArg }) => {
      const res = await axiosInstance.post(`/comp/submitSelection/${arg.jobId}`, { talentIds: arg.talentIds });
      return res.data as SubmitSelectionResponse;
    }
  );

  return {
    data,
    submitSelection: trigger,
    isLoading: isMutating,
    error,
  };
};

export const useCurrentSelectionQuery = (jobId?: string) => {
  const { data, error, isLoading, mutate } = useSWR<any, AxiosError>(
    jobId ? `/comp/currentSelection/${jobId}` : null,
    (url: string) => axiosInstance.get(url).then((res) => res.data)
  );

  return {
    currentSelection: (data?.data ?? []) as any[],
    isPaid: Boolean(data?.is_paid),
    round: Number(data?.round ?? 0),
    reshuffleCount: Number(data?.reshuffle_count ?? 0),
    isLoading,
    error,
    getCurrentSelection: mutate,
  };
};
